import { Close } from '@mui/icons-material';
import { Box, Button, Select, Stack, MenuItem } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import useAppLangs from '../../../../app/features/lang/hooks/useAppLangs';
import { useAppLang } from '../../../../app/features/lang/providers/AppLangProvider';
import { useAppSettings } from '../../../../app/features/settings/providers/AppSettingsProvider';
import langInvestWordLang from '../../../investor/langs/langInvestWordLang';
import useUserProfile from '../../hooks/useUserProfile';
import useUserSettingRoundings from '../../hooks/useUserSettingRoundings';
import useUserUpdateSettings from '../../hooks/useUserUpdateSettings';
import IUserProfile from '../../interfaces/IUserProfile';
import langUserProfile from '../../langs/langUserProfile';
import { FieldBox } from '../../pages/PUserProfile';
import { langLookup } from '../../../../app/utils/langLookup';
type VUserProfileSettingsProps = {
	sxStyle: SxProps;
	sxEditButton: SxProps;
	sxSelect: SxProps;
	disableEdit: boolean;
};
const VUserProfileSettings = (props: VUserProfileSettingsProps) => {
	const { sxStyle, sxEditButton, sxSelect, disableEdit } = props;

	const appLangs = useAppLangs();
	const investorSettingRoundings = useUserSettingRoundings();
	const appSettings = useAppSettings();
	const appLang = useAppLang();

	const { t } = useTranslation();

	const [editBlock, setEditBlock] = useState(false);

	const handleClickEditSettings = () => {
		setEditBlock(!editBlock);
	};

	const { mutate: updateSettings } = useUserUpdateSettings();

	const handleClickSaveSettings = () => {
		if (!profileData) return;
		updateSettings(
			{
				defaultLanguage: profileData.defaultLanguage ?? '',
				defaultRounding: profileData.defaultRounding ?? '',
				realizationStatus: profileData.realizationStatus ?? '',
			},
			{
				onSuccess: () => {
					// ALready UPDATE automatically the values with FEATURE LANG/STATUS/ROUNDING in Investor.tsx
					setEditBlock(!editBlock);
				},
			},
		);
	};

	const { investorProfile } = useUserProfile();

	const [profileData, setProfileData] = useState<IUserProfile>();

	useEffect(() => {
		if (investorProfile && appSettings && appLang) {
			setProfileData({
				...investorProfile,
				defaultRounding: appSettings.rounding,
				defaultLanguage: appLang.language,
				realizationStatus: appSettings.realizationStatus,
			} as IUserProfile);
		}
	}, [investorProfile, appSettings, appLang]);

	return (
		<>
			{profileData ? (
				<CAppPaper sx={sxStyle} variant="outlined">
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box className="title">
							<AppCustomIcon tag="user_settings" className="icon" />{' '}
							{t(langUserProfile.title_settings.tag)}
						</Box>
						<Box>
							{editBlock ? (
								<>
									<Button
										variant="text"
										color="secondary"
										size="small"
										sx={{ ...sxEditButton, marginRight: '20px' }}
										type="button"
										startIcon={<Close />}
										onClick={() => handleClickEditSettings()}
									>
										{t(langUserProfile.btn_cancel.tag)}
									</Button>
									<Button
										variant="contained"
										color="secondary"
										id="btn-save-settings"
										size="small"
										sx={sxEditButton}
										disabled={disableEdit}
										startIcon={<AppCustomIcon tag="save" className="icon" />}
										onClick={() => handleClickSaveSettings()}
									>
										{t(langUserProfile.btn_save.tag)}
									</Button>
								</>
							) : (
								<Button
									variant="text"
									color="secondary"
									id="btn-edit-settings"
									size="small"
									sx={sxEditButton}
									disabled={disableEdit}
									startIcon={<AppCustomIcon tag="edit" className="icon" />}
									onClick={() => handleClickEditSettings()}
								>
									{t(langUserProfile.btn_edit.tag)}
								</Button>
							)}
						</Box>
					</Stack>
					{editBlock ? (
						<>
							<Box>
								<FieldBox
									label={t(langUserProfile.field_rounding.tag)}
									value={(() => {
										const tag = langLookup(
											langInvestWordLang,
											'rounding_' +
												investorSettingRoundings.find((x) => x.tag === appSettings.rounding)
													?.tag,
										)?.tag;
										return appSettings.rounding && tag ? t(tag) : '?';
									})()}
									bEdit={editBlock}
									component={
										<Select
											fullWidth
											size="small"
											id="select-settings-rounding"
											value={profileData.defaultRounding}
											onChange={(e) =>
												setProfileData(
													(prevData) =>
														({
															...(prevData as IUserProfile),
															defaultRounding: e.target.value,
														} as IUserProfile),
												)
											}
											sx={sxSelect}
										>
											{investorSettingRoundings.map((investorSettingRounding, index) => {
												return (
													<MenuItem
														key={index}
														value={investorSettingRounding.tag}
														id={`select-settings-rounding-${investorSettingRounding.tag}`}
													>
														{(() => {
															const tag = langLookup(
																langInvestWordLang,
																'rounding_' + investorSettingRounding.tag,
															)?.tag;
															return tag ? t(tag) : investorSettingRounding.tag;
														})()}
													</MenuItem>
												);
											})}
										</Select>
									}
								/>
								<FieldBox
									label={t(langUserProfile.field_language.tag)}
									value={
										appLangs && appLangs.find((x) => x.code_iso2 === appLang.language)
											? appLangs.find((x) => x.code_iso2 === appLang.language)?.name
											: ''
									}
									bEdit={editBlock}
									component={
										<Select
											fullWidth
											id="select-settings-language"
											size="small"
											value={profileData.defaultLanguage}
											onChange={(e) =>
												setProfileData(
													(prevData) =>
														({
															...(prevData as IUserProfile),
															defaultLanguage: e.target.value,
														} as IUserProfile),
												)
											}
											sx={sxSelect}
										>
											{appLangs.map((lang, index) => {
												return (
													<MenuItem
														key={index}
														value={lang.code_iso2}
														id={`select-settings-language-${lang.code_iso2}`}
													>
														{lang.name}
													</MenuItem>
												);
											})}
										</Select>
									}
								/>
								<FieldBox
									label={t(langUserProfile.field_realization_status.tag)}
									bEdit={editBlock}
									value={
										appSettings.realizationStatus === 'all'
											? t(langInvestWordLang.all_funds.tag)
											: appSettings.realizationStatus === 'realized'
											? t(langInvestWordLang.realized_funds.tag)
											: t(langInvestWordLang.unrealized_funds.tag)
									}
									component={
										<Select
											variant="outlined"
											fullWidth
											size="small"
											value={profileData.realizationStatus}
											id="select-settings-realization-status"
											onChange={(e) =>
												setProfileData(
													(prevData) =>
														({
															...(prevData as IUserProfile),
															realizationStatus: e.target.value,
														} as IUserProfile),
												)
											}
											sx={sxSelect}
										>
											<MenuItem value="all" id="select-settings-realization-status-all">
												{t(langInvestWordLang.all_funds.tag)}
											</MenuItem>
											<MenuItem value="realized" id="select-settings-realization-status-realized">
												{t(langInvestWordLang.realized_funds.tag)}
											</MenuItem>
											<MenuItem
												value="unrealized"
												id="select-settings-realization-status-unrealized"
											>
												{t(langInvestWordLang.unrealized_funds.tag)}
											</MenuItem>
										</Select>
									}
								/>
							</Box>
						</>
					) : (
						<>
							<Box sx={{ marginTop: '20px' }}>{t(langUserProfile.label_for_block_settings.tag)}</Box>
						</>
					)}
				</CAppPaper>
			) : (
				<></>
			)}
		</>
	);
};

export default VUserProfileSettings;
